import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { generatePreviewHTML } from './generatePreviewHTML';
import { usePreviewBridge } from './usePreviewBridge';
import { ElementToolbar } from './ElementToolbar';
import { AddSiblingButton } from './AddSiblingButton';
import { DESKTOP_CANVAS_WIDTH } from '../BreakpointToggle';
import { getComponentDefinition } from '../../componentDefinitions';
import { Plus, LayoutTemplate } from 'lucide-react';

// Canvas widths for each breakpoint
const CANVAS_WIDTHS = {
  base: 375,      // Mobile
  tablet: 768,    // Tablet
  desktop: DESKTOP_CANVAS_WIDTH,  // Desktop
};

/**
 * IframePreviewCanvas - Renders page preview in an isolated iframe
 *
 * The iframe width is set to the exact breakpoint width, ensuring CSS media queries
 * respond correctly regardless of the actual browser window size.
 * Zoom is applied via CSS transform on the wrapper, not inside the iframe.
 *
 * @param {Object} props
 * @param {Array} props.sections - Page sections to render
 * @param {Object} props.pageConfig - Page configuration (background color, etc.)
 * @param {string} props.breakpoint - Current breakpoint: 'base' | 'tablet' | 'desktop'
 * @param {number} props.zoomLevel - Zoom level percentage (25-200)
 * @param {string} props.selectedSectionId - Currently selected section ID
 * @param {string} props.selectedElementPath - Currently selected element path
 * @param {Object} props.selectedComponent - Currently selected component object
 * @param {Function} props.onSectionSelect - Callback when section is clicked
 * @param {Function} props.onElementSelect - Callback when element is clicked
 * @param {Function} props.onDuplicateElement - Callback to duplicate selected element
 * @param {Function} props.onDeleteElement - Callback to delete selected element
 * @param {Function} props.onDeleteWithConfirmation - Callback to delete selected element/section with confirmation
 * @param {Function} props.onForceDelete - Callback to force delete selected element/section without confirmation
 * @param {Function} props.onAddComponent - Callback to add component inside selected element
 * @param {Function} props.onMoveUp - Callback to move element up
 * @param {Function} props.onMoveDown - Callback to move element down
 * @param {Function} props.onZoom - Callback for pinch-to-zoom events from inside iframe
 * @param {Function} props.onInlineEditEnd - Callback when inline text editing ends with new content
 * @param {Function} props.onChangeMedia - Callback to change media (for image elements)
 * @param {Function} props.onAddBlankSection - Callback to add a blank section
 * @param {Function} props.onOpenTemplateLibrary - Callback to open the template library
 */
export function IframePreviewCanvas({
  sections,
  pageConfig,
  breakpoint = 'desktop',
  zoomLevel = 100,
  selectedSectionId,
  selectedElementPath,
  selectedComponent,
  onSectionSelect,
  onElementSelect,
  onDuplicateElement,
  onDeleteElement,
  onDeleteWithConfirmation,
  onForceDelete,
  onAddComponent,
  onMoveUp,
  onMoveDown,
  onZoom,
  onInlineEditEnd,
  onChangeMedia,
  onAddSiblingAfter,
  onAddBlankSection,
  onOpenTemplateLibrary,
  placementMode,
  onPlacementComplete,
  onPlacementCancel,
  onSectionPlacementComplete,
}) {
  const iframeRef = useRef(null);
  const selectedSectionIdRef = useRef(selectedSectionId);
  const selectedElementPathRef = useRef(selectedElementPath);
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const canvasWidth = CANVAS_WIDTHS[breakpoint] || CANVAS_WIDTHS.desktop;

  // Keep refs in sync with current selection values
  useEffect(() => {
    selectedSectionIdRef.current = selectedSectionId;
    selectedElementPathRef.current = selectedElementPath;
  }, [selectedSectionId, selectedElementPath]);

  const handleInlineEditStart = useCallback(() => {
    setIsInlineEditing(true);
  }, []);

  const handleInlineEditEnd = useCallback((sectionId, elementPath, newContent) => {
    setIsInlineEditing(false);
    onInlineEditEnd?.(sectionId, elementPath, newContent);
  }, [onInlineEditEnd]);

  const handleInlineEditCancel = useCallback(() => {
    setIsInlineEditing(false);
  }, []);

  // Generate HTML content for iframe
  const htmlContent = useMemo(() => {
    return generatePreviewHTML(sections, pageConfig);
  }, [sections, pageConfig]);

  // Setup communication bridge
  const { updateSelection, scrollToElement, enterPlacementMode, exitPlacementMode } = usePreviewBridge(iframeRef, {
    onSectionSelect,
    onElementSelect,
    onZoom,
    onInlineEditStart: handleInlineEditStart,
    onInlineEditEnd: handleInlineEditEnd,
    onInlineEditCancel: handleInlineEditCancel,
    onDuplicate: onDuplicateElement,
    onDelete: onDeleteWithConfirmation,
    onForceDelete,
    onPlacementClick: onPlacementComplete,
    onPlacementCancel,
    onSectionPlacementClick: onSectionPlacementComplete,
  });

  // Inject HTML content into iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(htmlContent);
    doc.close();

    // Iframe rewrite destroys any active editing state
    setIsInlineEditing(false);

    // Auto-resize iframe height to content
    const resizeObserver = new ResizeObserver(() => {
      const body = doc.body;
      if (body) {
        iframe.style.height = `${body.scrollHeight}px`;
      }
    });

    // Wait for content to load before observing
    setTimeout(() => {
      if (doc.body) {
        resizeObserver.observe(doc.body);
        iframe.style.height = `${doc.body.scrollHeight}px`;
        // Re-apply selection highlight after iframe content is rewritten
        updateSelection(selectedSectionIdRef.current, selectedElementPathRef.current);
        // Ensure clean placement state after iframe rebuild
        exitPlacementMode();
        // Trigger toolbar repositioning now that iframe content is ready
        window.dispatchEvent(new Event('resize'));
      }
    }, 100);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  // Update selection highlight and scroll into view when selection changes
  useEffect(() => {
    updateSelection(selectedSectionId, selectedElementPath);
    scrollToElement(selectedSectionId, selectedElementPath);
  }, [selectedSectionId, selectedElementPath, updateSelection, scrollToElement]);

  // Sync placement mode with iframe
  const prevPlacementMode = useRef(null);
  useEffect(() => {
    if (placementMode && !prevPlacementMode.current) {
      // Entering placement mode — delay to ensure iframe JS is ready
      const timer = setTimeout(() => enterPlacementMode(placementMode.componentType, placementMode.placementLevel || 'component'), 150);
      prevPlacementMode.current = placementMode;
      return () => clearTimeout(timer);
    } else if (!placementMode && prevPlacementMode.current) {
      // Exiting placement mode
      exitPlacementMode();
      prevPlacementMode.current = null;
    } else {
      prevPlacementMode.current = placementMode;
    }
  }, [placementMode, enterPlacementMode, exitPlacementMode]);

  // Escape key handler for placement mode (parent-side)
  useEffect(() => {
    if (!placementMode) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onPlacementCancel?.();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [placementMode, onPlacementCancel]);

  // Calculate scale for zoom
  const scale = zoomLevel / 100;

  // Get element type and check if it can have children
  const elementType = selectedComponent?.type || null;
  const definition = elementType ? getComponentDefinition(elementType) : null;
  const canAddChildren = definition?.canHaveChildren === true;

  const isEmpty = !sections || sections.length === 0;

  return (
    <div
      className="iframe-preview-container"
      style={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '24px',
        backgroundColor: '#e5e7eb',
        position: 'relative',
      }}
    >
      <div
        className="iframe-preview-zoom-wrapper"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          transition: 'transform 0.15s ease-out',
        }}
      >
        <iframe
          ref={iframeRef}
          title="Page Preview"
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: `${canvasWidth}px`,
            minHeight: '600px',
            height: 'auto',
            border: breakpoint !== 'desktop' ? '1px solid #d1d5db' : 'none',
            borderRadius: breakpoint === 'base' ? '24px' : breakpoint === 'tablet' ? '0px' : '0px',
            backgroundColor: 'white',
            display: 'block',
          }}
        />
      </div>

      {/* Empty state overlay — sits on top of the canvas */}
      {isEmpty && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            className="bg-white border border-dashed border-gray-300 rounded-lg shadow-sm"
            style={{
              pointerEvents: 'auto',
              width: `${Math.min(canvasWidth * (scale), 420)}px`,
              padding: '48px 32px',
            }}
          >
            <div className="flex flex-col items-center gap-5">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <Plus className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Start building your page</p>
                <p className="text-xs text-gray-400 mt-1">Add your first section to get started</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onAddBlankSection}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Blank section
                </button>
                <button
                  onClick={onOpenTemplateLibrary}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <LayoutTemplate className="w-3.5 h-3.5" />
                  From template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Placement mode banner */}
      {placementMode && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50,
          }}
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-full shadow-lg">
            <span>Click to place <strong>{placementMode.componentType}</strong></span>
            <span className="opacity-60">·</span>
            <button
              onClick={onPlacementCancel}
              className="opacity-80 hover:opacity-100 underline transition-opacity"
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit' }}
            >
              Esc to cancel
            </button>
          </div>
        </div>
      )}

      {/* Element Toolbar - floating toolbar for selected elements (hidden during inline editing) */}
      {selectedElementPath && !placementMode && !isInlineEditing && (
        <>
          <ElementToolbar
            iframeRef={iframeRef}
            selectedElementPath={selectedElementPath}
            selectedSectionId={selectedSectionId}
            zoomLevel={zoomLevel}
            elementType={elementType}
            canAddChildren={canAddChildren}
            onDuplicate={onDuplicateElement}
            onDelete={onDeleteElement}
            onAddComponent={onAddComponent}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onChangeMedia={onChangeMedia}
          />
          <AddSiblingButton
            iframeRef={iframeRef}
            selectedElementPath={selectedElementPath}
            selectedSectionId={selectedSectionId}
            zoomLevel={zoomLevel}
            onClick={onAddSiblingAfter}
          />
        </>
      )}
    </div>
  );
}

export default IframePreviewCanvas;
